<template>
  <v-container fluid class="px-0">
    <v-row>
      <v-col cols="12">
        <page-header
          icon="mdi-cog"
          :title="$t('settings')"
          :subtitle="$t('manage_restaurant_settings')"
        />
      </v-col>
    </v-row>

    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <v-card flat class="mb-6">
      <v-tabs v-model="activeTab" background-color="transparent" grow>
        <v-tab>{{ $t("contracts") }}</v-tab>
        <v-tab>{{ $t("guest_settings") }}</v-tab>
        <v-tab>{{ $t("general_settings") }}</v-tab>
      </v-tabs>
      <v-divider />
      <v-tabs-items v-model="activeTab" class="pt-4">
        <v-tab-item class="px-2">
          <corporate-settings-tab :settings="settings" />
        </v-tab-item>

        <v-tab-item class="px-2">
          <guest-settings-tab :settings="settings" />
        </v-tab-item>

        <v-tab-item class="px-2">
          <general-settings-tab
            v-model="alphabet"
            :settings="settings"
            :restaurant-id="$store.state.restaurant.id"
            @select-image="openImagePicker"
            @remove-image="removeImageConfirm"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-card flat class="save-bar">
      <v-card-text class="py-3">
        <v-row align="center">
          <v-spacer />
          <v-col cols="12" sm="5" md="4" class="d-flex justify-end">
            <primary-button @click="save" :loading="loading">
              {{ $t("record") }}
            </primary-button>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <image-gallery-dialog
      v-model="gallery"
      :restaurant-id="$store.state.restaurant.id"
      @select-image="selectImage"
    />
  </v-container>
</template>

<script>
import restaurantService from "@/services/restaurant";
import hasSnackbar from "@/mixins/hasSnackbar";
import { createRestaurantSettings } from "@/models/restaurantSettings";
import PageHeader from "@/components/global/PageHeader.vue";
import CorporateSettingsTab from "@/components/restaurants/settings/tabs/CorporateSettingsTab.vue";
import GuestSettingsTab from "@/components/restaurants/settings/tabs/GuestSettingsTab.vue";
import GeneralSettingsTab from "@/components/restaurants/settings/tabs/GeneralSettingsTab.vue";
import ImageGalleryDialog from "@/components/restaurants/settings/dialogs/ImageGalleryDialog.vue";

export default {
  components: {
    PageHeader,
    CorporateSettingsTab,
    GuestSettingsTab,
    GeneralSettingsTab,
    ImageGalleryDialog,
  },
  mixins: [hasSnackbar],
  data() {
    return {
      activeTab: 0,
      loading: false,
      settings: createRestaurantSettings(),
      alphabet: "",
      gallery: false,
      selectedImageType: null,
    };
  },
  created() {
    const restaurant = this.$store?.state?.restaurant;
    if (!restaurant) {
      return;
    }

    this.settings = {
      ...this.settings,
      ...restaurant.settings,
      ekavica: restaurant.lang === "ekavica",
      ijekavica: restaurant.lang === "ijekavica",
    };

    this.alphabet =
      restaurant.alphabet === "cir" || restaurant.alphabet === "lat"
        ? restaurant.alphabet
        : "";
  },
  methods: {
    openImagePicker(type) {
      this.selectedImageType = type;
      this.gallery = true;
    },
    save() {
      this.loading = true;
      restaurantService
        .updateRestaurantSettings(this.$store.state.restaurant.id, this.settings)
        .then((res) => {
          this.showSuccessSnackbar(this.$t("settings_saved"));
          setTimeout(() => {
            location.reload();
          }, 1500);
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_saving_settings"));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    removeImageConfirm(imageType) {
      const imageTypeNames = {
        logo: this.$t("logo"),
        header: this.$t("header"),
        restoran_image: this.$t("restaurant_image"),
        logo_mob_header: this.$t("mobile_header_banner"),
        logo_mob_footer: this.$t("mobile_footer_banner"),
        logo_mob_restoran: this.$t("mobile_restaurant_image"),
      };

      this.$refs.confirmBox
        .open(
          this.$t("remove_image"),
          this.$t("remove_branding_confirmation", { type: imageTypeNames[imageType] }),
          { color: "warning" }
        )
        .then(() => {
          this.settings[imageType] = null;
          this.showSuccessSnackbar(this.$t("image_removed"));
        })
        .catch(() => {
          // User cancelled
        });
    },
    selectImage(image) {
      this.gallery = false;
      if (this.selectedImageType) {
        this.settings[this.selectedImageType] = image.name;
        this.selectedImageType = null;
      }
    },
  },
  watch: {
    "settings.mobColor.primary": {
      handler: function (val, oldVal) {
        this.settings.mob.primary = val ? val.hex : null;
      },
      deep: true,
    },
    "settings.mobColor.text_toolbar_color": {
      handler: function (val, oldVal) {
        this.settings.mob.text_toolbar_color = val ? val.hex : null;
      },
      deep: true,
    },
    "settings.mobColor.text_color": {
      handler: function (val, oldVal) {
        this.settings.mob.text_color = val ? val.hex : null;
      },
      deep: true,
    },
    "settings.mobColor.button_color": {
      handler: function (val, oldVal) {
        this.settings.mob.button_color = val ? val.hex : null;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.save-bar {
  position: sticky;
  bottom: 0;
  z-index: 3;
  background: #fff;
  border-top: 1px solid #eee;
  margin-top: 16px;
}
</style>
